/**
 * Per-cell SVG paths with beveled or rounded corners on the outer boundary of each
 * same-fill region (no chamfer along an edge between two cells of the same color).
 * Coordinates: unit square [col, col+1] × [row, row+1] in viewBox space.
 */

export type CellCornerMode = "square" | "bevel" | "round";

function fmt(n: number): string {
    const s = n.toFixed(5);
    return s.replace(/\.?0+$/, "") || "0";
}

function fillAt(
    fills: ReadonlyArray<string>,
    gridCols: number,
    gridRows: number,
    c: number,
    r: number
): string | undefined {
    if (c < 0 || c >= gridCols || r < 0 || r >= gridRows) return undefined;
    return fills[r * gridCols + c];
}

/**
 * Which corners of this cell lie on a convex outer corner of the union of same-fill cells.
 * E.g. four same-color cells in a 2×2 block: only A_tl, B_tr, C_bl, D_br.
 */
function outerCornerFlags(
    col: number,
    row: number,
    gridCols: number,
    gridRows: number,
    fills: ReadonlyArray<string>
): { tl: boolean; tr: boolean; br: boolean; bl: boolean } {
    const here = fills[row * gridCols + col];
    const same = (c: number, r: number) =>
        fillAt(fills, gridCols, gridRows, c, r) === here;

    const leftSame = same(col - 1, row);
    const rightSame = same(col + 1, row);
    const aboveSame = same(col, row - 1);
    const belowSame = same(col, row + 1);

    return {
        tl: !leftSame && !aboveSame,
        tr: !rightSame && !aboveSame,
        br: !rightSame && !belowSame,
        bl: !leftSame && !belowSame,
    };
}

function clampCornerSize(
    b: number,
    { tl, tr, br, bl }: { tl: boolean; tr: boolean; br: boolean; bl: boolean }
): number {
    let m = Math.min(Math.max(0, b), 0.49);
    if (m <= 0) return 0;
    if (tl && tr) m = Math.min(m, 0.5 - 1e-4);
    if (bl && br) m = Math.min(m, 0.5 - 1e-4);
    if (tl && bl) m = Math.min(m, 0.5 - 1e-4);
    if (tr && br) m = Math.min(m, 0.5 - 1e-4);
    return m;
}

/**
 * Closed path for one cell, or null to render as a plain `<rect>`.
 * `fills` — `gridCols * gridRows` palette strings in row-major order; required when mode ≠ square.
 */
export function buildLuminanceCellPathD(
    col: number,
    row: number,
    gridCols: number,
    gridRows: number,
    cornerSize: number,
    mode: CellCornerMode,
    fills: ReadonlyArray<string>
): string | null {
    if (mode === "square") return null;

    const cf = outerCornerFlags(col, row, gridCols, gridRows, fills);
    const b = clampCornerSize(cornerSize, cf);
    if (b <= 0) return null;

    const { tl, tr, br, bl } = cf;
    const c = col;
    const r = row;

    const sx = tl ? c + b : c;
    const sy = r;
    const parts: string[] = [`M${fmt(sx)} ${fmt(sy)}`];

    // Top edge → toward TR
    const topEndX = c + 1 - (tr ? b : 0);
    parts.push(`L${fmt(topEndX)} ${fmt(r)}`);

    // TR corner
    if (tr) {
        if (mode === "bevel") {
            parts.push(`L${fmt(c + 1)} ${fmt(r + b)}`);
        } else {
            parts.push(
                `A${fmt(b)} ${fmt(b)} 0 0 1 ${fmt(c + 1)} ${fmt(r + b)}`
            );
        }
    }

    // Right edge
    const rightEndY = r + 1 - (br ? b : 0);
    parts.push(`L${fmt(c + 1)} ${fmt(rightEndY)}`);

    // BR corner
    if (br) {
        if (mode === "bevel") {
            parts.push(`L${fmt(c + 1 - b)} ${fmt(r + 1)}`);
        } else {
            parts.push(
                `A${fmt(b)} ${fmt(b)} 0 0 1 ${fmt(c + 1 - b)} ${fmt(r + 1)}`
            );
        }
    }

    // Bottom edge
    const botEndX = c + (bl ? b : 0);
    parts.push(`L${fmt(botEndX)} ${fmt(r + 1)}`);

    // BL corner
    if (bl) {
        if (mode === "bevel") {
            parts.push(`L${fmt(c)} ${fmt(r + 1 - b)}`);
        } else {
            parts.push(`A${fmt(b)} ${fmt(b)} 0 0 1 ${fmt(c)} ${fmt(r + 1 - b)}`);
        }
    }

    // Left edge
    const leftEndY = r + (tl ? b : 0);
    parts.push(`L${fmt(c)} ${fmt(leftEndY)}`);

    // TL corner (back to start)
    if (tl) {
        if (mode === "bevel") {
            parts.push(`L${fmt(sx)} ${fmt(sy)}`);
        } else {
            parts.push(`A${fmt(b)} ${fmt(b)} 0 0 1 ${fmt(sx)} ${fmt(sy)}`);
        }
    }

    parts.push("Z");
    return parts.join("");
}
